import {
  AfterViewChecked, AfterViewInit,
  Directive,
  EventEmitter,
  HostBinding,
  Input, NgZone, OnDestroy, OnInit,
  Output,
  QueryList, ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import { LMGlobalPersonalization, LMGlobalPersonalizationService } from '@app/states';
import { EMPTY, Subject } from 'rxjs';
import {
  LMApplicationMenuService,
  LMApplicationMenuTransactionService,
  LMCtrlShiftClickEvent,
  LMHtmlUrlDocComponent
} from '@app/components';
import {
  LMBusyIndicatorService, LMComponentInfoService,
  LMContextService,
  LMErrorUtilService,
  LMLoggerService,
  LMRouteComponentService,
  LMSpecService, LMUrlUtilService
} from '@app/services';
import { AppConstants, LMBrowserUtilService, LMContextUtilService, LMGlobalUtilService } from '@app/utils';
import { LMApplicationMenuSubMenuService } from '@components/applicationmenu/submenu/lm-application-menu-submenu.service';
import { switchMap, takeUntil } from 'rxjs/operators';
import * as _ from 'lodash';
import { LMThemeService } from '@banner/lm-theme.service';

@Directive()
export abstract class LMApplicationMenuSubmenuBaseDirective implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @HostBinding('style.display') display = 'block';

  @Input() inSearchResultsMenu: false;
  @Input() applicationMenuSpec: Array<LMMenuItemSpec>;
  @Output() menuStateRestored: EventEmitter<any> = new EventEmitter<any>();
  @Output() menuLoaded: EventEmitter<any> = new EventEmitter<any>();

  @ViewChildren('accordionHeader', { read: ViewContainerRef }) accordionHeaders: QueryList<ViewContainerRef>;
  @ViewChild('menuFormDialog', { read: ViewContainerRef, static: true }) placeholder: ViewContainerRef;

  public settings: LMGlobalPersonalization = {};
  private updateRequired: boolean;
  private throttleId: string = null; // this is used to avoid double clicks!!!

  private menuItemClicked$ = new Subject<any>();
  private pause$ = new Subject<boolean>();
  private destroy$ = new Subject<boolean>();

  protected constructor(
    protected lmApplicationMenuService: LMApplicationMenuService,
    protected lmApplicationMenuTransactionService: LMApplicationMenuTransactionService,
    protected lmErrorUtilService: LMErrorUtilService,
    protected lmLoggerService: LMLoggerService,
    protected lmSpecService: LMSpecService,
    protected lmContextService: LMContextService,
    protected lmContextUtilService: LMContextUtilService,
    protected routeComponent: LMRouteComponentService,
    protected lmUrlUtilService: LMUrlUtilService,
    protected lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    protected lmGlobalUtilService: LMGlobalUtilService,
    protected lmApplicationMenuSubMenuService: LMApplicationMenuSubMenuService,
    protected lmBusyIndicatorService: LMBusyIndicatorService,
    protected ngZone: NgZone,
    protected lmComponentInfoService: LMComponentInfoService,
    protected lmBrowserUtil: LMBrowserUtilService,
    protected lmThemeService: LMThemeService
  ) {
    this.lmLoggerService.trace('@ApplicationMenuSubMenuComponent -> constructor');
  }

  ngOnInit() {
    if (this.lmApplicationMenuService.loadAllLevels()) {
      this.clearSubMenus(this.applicationMenuSpec);
    }
  }

  ngAfterViewInit() {
    this.updateRequired = true;
    this.subscribeToMenuItemClick();
  }

  ngAfterViewChecked() {
    if (this.updateRequired && this.lmApplicationMenuService.lastOpenedMenuId && this.accordionHeaders.length > 0) {
      const lastMenu = this.accordionHeaders.filter((header => header.element.nativeElement.id === this.lmApplicationMenuService.lastOpenedMenuId));
      if (lastMenu.length > 0) {
        this.lmApplicationMenuService.setLastOpenedMenu(lastMenu[ 0 ].element.nativeElement);
        this.updateRequired = false;
      }
    }
  }

  public trackById(index: number, menuItem: any): string {
    return menuItem.id;
  }

  private throttleClickEvent(menuItem: any): boolean {
    if (this.throttleId && this.throttleId === menuItem.id) {
      return true;
    }

    this.throttleId = menuItem.id;

    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.throttleId = null;
      }, 500);
    });
    return false;
  }

  private subscribeToMenuItemClick() {
    const pausable$ = this.pause$
      .pipe(
			  switchMap(paused => paused ? EMPTY : this.menuItemClicked$),
			  takeUntil(this.destroy$)
      );

    // this.pausableSubscription = pausable$.subscribe( (menuItem) => this.doMenuItemClicked(menuItem) );
    pausable$.subscribe( (menuItem) => {
      // this.menuTimerSubscription.unsubscribe();
      this.doMenuItemClicked(menuItem);
    });

    this.pause$.next(false);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
    // this.pausableSubscription.unsubscribe();
    this.pause$.complete();
    this.menuItemClicked$.complete();
  }

  public loadMenu(event: MouseEvent, menuItem: LMMenuItemSpec): void {
    if (menuItem.expanded) {
      return;
    }

    if (this.lmApplicationMenuService.loadAllLevels()) {
      const allLevelsResponse = _.cloneDeep(this.lmApplicationMenuService.getAllLevelsResponse());
      const menuItemSpecMenu = [];
      const self = this;

      if (allLevelsResponse) {
        // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
        allLevelsResponse.menuSpecs.forEach(function callback(menuItemSpec) {
          if (menuItemSpec.menuIndex === menuItem.menuIndex) {
            menuItemSpecMenu.push(menuItemSpec.menu);
            self.clearSubMenus(menuItemSpec.menu);
          }

          if (menuItemSpec.menu && menuItemSpec.menu.length > 0) {
            menuItemSpec.menu.forEach(callback);
          }
        });

        menuItem.menu = menuItemSpecMenu[0];
      }

      menuItem.expanded = true;

      this.lmApplicationMenuService.saveExpandedMenus(menuItem);
      this.onMenuLoaded(event);
    } else {
      this.lmSpecService.getApplicationMenuSpec(menuItem.link).subscribe((response: LMApplicationMenuSpec) => {
        menuItem.menu = response.menuSpecs;
        menuItem.expanded = true;

        this.lmApplicationMenuService.saveExpandedMenus(menuItem);
        this.onMenuLoaded(event);
      });
    }
  }

  public onMenuLoaded(event): void {
    this.menuLoaded.emit(event);
  }

  /**
   * Set href url when a right click is executed
   * @param event
   * @param menuItem
   */
  public onMenuItemRightClicked(event: MouseEvent, menuItem: LMMenuItemSpec) {
    if (menuItem && menuItem.link) {
      (event.currentTarget as HTMLElement).setAttribute('href', menuItem.link);
    }
  }

  public onMenuItemClicked(event: MouseEvent, menuItem: any) {
    // Reset href to hash for regular click events.
    (event.target as HTMLElement).setAttribute('href', '#');
    this.lmApplicationMenuService.saveLastMenuItemClicked(menuItem.menuIndex);

    if ((event.ctrlKey || event.metaKey) && event.shiftKey) {
      event.stopPropagation();
      // event.preventDefault();
      const isMenu = menuItem.menu !== false;
      const configInfo = menuItem.menu[menuItem.menuIndex] ? menuItem.menu[menuItem.menuIndex].configInfo : undefined;
      const compConfigInfo: ConfigInfo = {};
      compConfigInfo['personalization'] = isMenu && configInfo ? configInfo.personalization : false;
      compConfigInfo['configuration'] = isMenu && configInfo ? configInfo.configuration : false;
      compConfigInfo['configStamp'] = configInfo && configInfo.configStamp ? configInfo.configStamp : 0;
      compConfigInfo['classConfigStamp'] = configInfo && configInfo.classConfigStamp ? configInfo.classConfigStamp : 0;

      const compInfo: LMComponentInfo = {
        componentType: 'MENU',
        type: isMenu ? 'menuItem' : 'menu',
        name: menuItem.name,
        developer: this.lmContextService.isDeveloper(),
        id: menuItem.id,
        label: menuItem.label,
        link: menuItem.link,
        menuIndex: menuItem.menuIndex,
        parent: menuItem.parent,
        parentLabel: menuItem.parentLabel,
        resourceId: menuItem.resourceId,
        resourceType: menuItem.resourceType,
        resourceInfo: menuItem.resourceInfo ? menuItem.resourceInfo : undefined,
        target: menuItem.target,
        lpl: menuItem.lpl,
        configInfo: compConfigInfo,
        hasConfigAccess: isMenu ? this.lmContextService.hasConfigAccess() : false,
      };
      this.lmComponentInfoService.displayComponentInfo(event as LMCtrlShiftClickEvent, this.placeholder, compInfo);
      return false;
      // this.displayComponentInfo(event);
    }

    if (this.throttleClickEvent(menuItem)) {
      return;
    }

    this.setLastOpenedMenu(menuItem);

    if (menuItem.menu) {
      return;
    }
    this.menuItemClicked$.next(menuItem);
  }

  private doMenuItemClicked(menuItem: any) {
    const routeComponent: LMRouteComponentService = this.routeComponent;

    // Pause menu clicks
    this.pause$.next(true);
    // Resume menu clicks after 20 seconds. This is to prevent ApplicationMenu from locking up
    // if a back end call failed for some reason and we did not catch it.
    // Challenging to get this working, every click creates a timer and it gets out of control.
    // this.menuTimerSubscription = timer(20000).subscribe(() => this.pause$.next(false));

    this.lmBusyIndicatorService.showBusyIndicator(); // Dialog open will hide the busy indicator
    this.lmSpecService.getApplicationMenuSpec(menuItem.link).subscribe(
      response => {
        if (response.resourceType === 'action') {
          if (!this.isAsOfDateInPast()) {
            this.lmApplicationMenuSubMenuService.runAction(response.dialogUrl || response.redirectUrl, response.dataView, this.placeholder, this.pause$);
          }
        } else if (response.dialogUrl) {
          const personalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
          if (this.lmContextService.is3_5Content(personalization.appDisplay)) {
            const comp: LMHtmlUrlDocComponent = routeComponent.getHtmlUrlDocComponent();
            comp.runActionFormIn3_5(response.dialogUrl);
            this.pause$.next(false);
          } else {
            if (!this.isAsOfDateInPast()) {
              this.lmApplicationMenuSubMenuService.requestActionFromPageMenu(response.dialogUrl, response.resourceId, this.placeholder, this.pause$);
            }
          }
        } else if (response.redirectUrl) {
          this.lmContextService.setClearNextPageState(true);
          const url = response.redirectUrl;
          const noDefaultPortUrl = this.lmUrlUtilService.stripDefaultPort(url);
          let host = this.lmContextUtilService.getLocation(noDefaultPortUrl).host;

          // In IE, the host value includes the port number, we need to strip it so it will match the window.location.host
          if (this.lmBrowserUtil.isIE) {
            host = this.lmUrlUtilService.stripDefaultPortFromHost(host);
          }

          const log = {
            codeLoc: 'lm-application-menu.component.td - onMenuItemClicked',
            url,
            noDefaultPortUrl,
            host,
            'window.location.host': window.location.host,
            'url.length': url.length,
            doHostsMatch: (host.toLowerCase() === window.location.host.toLowerCase()),
          };
          this.lmLoggerService.log(log);

          if (response.external) {
            this.lmUrlUtilService.openExternalUrl(response.redirectUrl);
          } else if (response.embedded) {
            if ((url.length > 0 && host !== window.location.host) || (response.redirectUrl.indexOf('.html') >= 0)) {
              const isHtmlFragment = response.redirectUrl.indexOf('standalone=true') < 0;
              const webapp = this.lmContextService.getContext().webApp;
              const dataArea = this.lmContextService.getContext().urlPdlOrDa;
              let autoLoadUrl = dataArea + '/' + webapp + '/html/url?autoLoadUrl=' + encodeURIComponent(response.redirectUrl);
              autoLoadUrl = this.lmUrlUtilService.updateContextSessionKeyParams(autoLoadUrl);
              this.lmGlobalUtilService.navigateByUrl(autoLoadUrl);
            } else {
              this.lmGlobalUtilService.navigateByUrl(noDefaultPortUrl);
            }
          } else {
            if ((url.length > 0 && host !== window.location.host) || (response.redirectUrl.indexOf('.html') >= 0)) {
              window.location.href = response.redirectUrl;
            } else {
              this.lmGlobalUtilService.navigateByUrl(noDefaultPortUrl);
            }
          }
          this.pause$.next(false);
        } else {
          this._handleError(response);
          this.pause$.next(false);
        }
      },
      error => {
        this.pause$.next(false);
        this._handleError(error);
      });
  }

  private isAsOfDateInPast(): boolean {
    if (this.lmContextService.hasAsOfDateInPast()) {
      this.lmGlobalUtilService.displayAsOfDateInPastMessage();
      this.pause$.next(false);
      this.lmBusyIndicatorService.hideBusyIndicator();
      return true;
    }
    return false;
  }

  private setLastOpenedMenu(menuItem: any) {
    this.lmApplicationMenuService.lastOpenedMenuId = menuItem.appMenuId; // this.getMenuItemId(menuItem);

    const applicationMenuPersonalization: LMGlobalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    if (!_.isEmpty(applicationMenuPersonalization)) {
      this.settings = applicationMenuPersonalization;
    }
    this.settings.lastOpenedMenu = this.lmApplicationMenuService.lastOpenedMenuId;

    this.savePersonalization();
  }

  public savePersonalization(): void {
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(this.settings);
  }

  private _handleError(error: any) {
    this.lmErrorUtilService.handleUnexpectedError(error, this.placeholder);
  }

  public menuIdV1(menuItem: any) {
    return this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1 ? menuItem.id : null;
  }

  public menuIdV2(menuItem: any) {
    return this.lmContextService.testVersion() >= AppConstants.TESTVERSION_V2 ? menuItem.id + '_menuitem' : null;
  }

  /**
   * Used for data-automation-id, this function will convert underscores _ and
   * _prd_ to a dash -
   * @param menuItem
   */
  public dataAutomationId(menuItem: LMBannerSearchResult) {
    // const automationId = menuItem.id.replace(/(_prd_)|(_)/g, '-');
    if (this.inSearchResultsMenu) {
      return `lm-${menuItem.automationId}-searchresult`;
    }
    return `lm-${menuItem.automationId}-menuitem`;
  }

  /**
   * Clear all menu arrays so the template does not render every level
   * @param menuSpec
   */
  private clearSubMenus(menuSpec: Array<LMMenuItemSpec>) {
    if (Array.isArray(menuSpec)) {
      menuSpec.map((menuSpecItem: LMMenuItemSpec) => {
        if (menuSpecItem.menu && !menuSpecItem.expanded) {
          menuSpecItem.menu = [];
        }

        return menuSpecItem;
      });
    }
  }
}
